// src/portal-app/insights/OperationalEfficiencyScreen.js
//
// W2 Task 9 — Operational Efficiency, the fourth native Insights surface
// and the FIRST with no web page behind it (the browser portal's
// OperationalEfficiencyPage is a "Coming soon" stub; the /operations
// endpoint has been live but orphaned). Designed from the endpoint's shape
// and the Task-5 kit around the question a manager actually asks here:
// "when am I busy, and who is carrying the shift?"
//
// W4 Task 14 (P6 — spec §5, conservative pass; NO structural rewrite, zone
// order and every chart unchanged):
//   • hero already carried the P4 GlassCard-veil treatment (HeroValue) —
//     confirmed, not re-authored;
//   • no ShareBar: this screen has no multi-number strip and no P&L-style
//     identity to convert. Staff Members / Peak Hour / Avg Txn/Hour don't
//     sum to Total Revenue the way Total Expenses + Net Profit do on
//     Financial Performance (Task 13) — nothing here is a proportion of a
//     shared whole, so there is nothing for ShareBar to replace;
//   • no CondensedRow: the only row-shaped renders are BarList (staff
//     leaderboard — a ranking chart, explicitly a "kept chart" per the
//     brief) and InsightList (typed info/success cards) — same conclusion
//     Task 13 reached for its own BarList/InsightList, neither matches
//     CondensedRow's single-value/single-pill/single-secondary contract;
//   • collapsed-empty: already satisfied — isEmptyPayload gates the whole
//     surface into the empty state when there is nothing to report, so no
//     zone here can render a run of dashes;
//   • PullToRefresh now wraps SurfaceStates (RangeBar stays outside both,
//     unchanged, so it keeps surviving loading/error/empty).
//
// Data — reused UNCHANGED from services/api.js:
//   reportingService.getOperationsV2(start, end)   the whole payload
// There is NO /narrative/operations endpoint — this surface deliberately
// renders no NarrativeCard.
//
// Zones (phone-first):
//   hero Total Revenue + DeltaPill   the cross-surface anchor: same hero
//                                    anatomy as Financial/Products/Costs so
//                                    the four surfaces read as one app, and
//                                    revenue is the volume the two zones
//                                    below decompose (by hour, by person);
//   KPI grid                         Staff Members / Peak Hour / Avg
//                                    Txn/Hour — Peak Hour is a STRING with
//                                    null comparisons (normaliseKpi's
//                                    isText → the tile renders the string
//                                    with no delta pill);
//   Revenue by Hour                  "when am I busy" — TrendChart over
//                                    sections.hourly with interior closed
//                                    hours zero-filled (kit fillHourlyGaps;
//                                    gold.hourly_sales has no zero-fill at
//                                    source) and the busiest hour visibly
//                                    marked on the curve (markerIndex);
//   Staff Leaderboard                "who is carrying the shift" — BarList
//                                    by revenue with share-of-revenue and a
//                                    factual transactions + avg-transaction
//                                    caption. Staff privacy: numbers only,
//                                    no judgemental labels; names are raw
//                                    labels, never t() keys;
//   Insights                         top-level data.insights (info/success).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header/back
// button (route meta in nav/routes.js).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { reportingService } from '../../services/api';
import { rangeForPreset, toLocalISO, trendLabel } from '../nav/dateRange';
import { useRangePreset } from './rangeState';
import {
    normaliseKpi,
    fmtMoney,
    fillHourlyGaps,
    HeroValue,
    RangeBar,
    KpiGrid,
    InsightList,
    BarList,
    TrendChart,
    SurfaceStates,
} from './kit';
import { PullToRefresh, ensureKitCss } from '../shell/kit';

/* Screen-local chrome only — everything data-bearing comes from the kit
   (which also injects the shared .insk-* stylesheet, incl. the HeroValue
   hero anatomy shared by all Insights surfaces). */
const CSS = `
.ops-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
`;

/* Backend KPI names are English literals — map them to t() keys.
   total_revenue already exists in the DB (shipped portal pages use it);
   staff_members / peak_hour / avg_txn_per_hour are registered by this
   branch. */
const KPI_LABELS = {
    'Total Revenue': ['total_revenue', 'Total Revenue'],
    'Staff Members': ['staff_members', 'Staff Members'],
    'Peak Hour': ['peak_hour', 'Peak Hour'],
    'Avg Txn/Hour': ['avg_txn_per_hour', 'Avg Txn/Hour'],
};

/* "Empty" = the period produced nothing to report: zero revenue, no hourly
   rows and no staff rows. (A period with staff activity but zero revenue
   still renders — the leaderboard and hours are decision-grade alone.) */
function isEmptyPayload(d) {
    const kpis = (d && d.kpis) || [];
    const rev = kpis.find((k) => k.name === 'Total Revenue');
    const sections = (d && d.sections) || {};
    const hasHourly = (sections.hourly || []).length > 0;
    const hasStaff = (sections.staff || []).length > 0;
    return !((rev && Number(rev.value)) || hasHourly || hasStaff);
}

export default function OperationalEfficiencyScreen() {
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const { t, language } = useLanguage();

    // Shared across all Insights surfaces (M1) — survives pops and tab
    // switches; the setter coerces junk ids so rangeForPreset never throws.
    const [preset, setPreset] = useRangePreset();
    const range = useMemo(() => rangeForPreset(preset), [preset]);
    const [status, setStatus] = useState('loading'); // loading|error|empty|ready
    const [data, setData] = useState(null);

    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    /* Main fetch — stale responses are dropped by sequence, so a quick
       range flick can never paint an older period over a newer one. */
    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        // Returned (W4 Task 14) so PullToRefresh can await it — its spinner
        // stays up until the fetch actually settles, success or failure.
        return reportingService.getOperationsV2(startIso, endIso)
            .then((d) => {
                if (seq !== seqRef.current) return;
                setData(d);
                setStatus(isEmptyPayload(d) ? 'empty' : 'ready');
            })
            .catch(() => {
                if (seq === seqRef.current) setStatus('error');
            });
    }, [startIso, endIso]);
    useEffect(() => { load(); }, [load]);

    const kpis = (data && data.kpis) || [];
    const sections = (data && data.sections) || {};

    /* ---- hero: Total Revenue + delta (normaliseKpi owns the quirks;
       rendering is the kit's HeroValue) ---- */
    const revKpi = kpis.find((k) => k.name === 'Total Revenue') || null;
    const revNorm = revKpi ? normaliseKpi(revKpi) : null;

    /* ---- KPI grid: the three non-hero KPIs. "Peak Hour" is a STRING with
       null comparisons — normaliseKpi's isText renders the raw string and
       suppresses the delta pill entirely; nothing re-derived here. An
       odd-count remainder spans its last tile full-width. ---- */
    const restKpis = revKpi ? kpis.filter((k) => k !== revKpi) : kpis;
    const gridItems = restKpis.map((k, i) => {
        const meta = KPI_LABELS[k.name];
        return {
            kpi: k,
            label: meta ? t(meta[0], {}, meta[1]) : k.name,
            format: 'number', // the only money KPI (Total Revenue) is the hero
            caption: true,
            ...(restKpis.length % 2 === 1 && i === restKpis.length - 1 ? { span: 2 } : {}),
        };
    });

    /* ---- "when am I busy": hourly revenue curve. Interior closed hours
       are zero-filled (gold.hourly_sales has no rows for them and
       TrendChart spaces points by index — an unfilled lunch closure would
       render as continuous trade); edges stay trimmed. The busiest hour
       is marked on the curve — derived from the same data the backend
       ranks for its Peak Hour KPI (max revenue), not parsed from the
       KPI's display string. ---- */
    const hourlyRows = fillHourlyGaps(sections.hourly);
    const hourlyPoints = hourlyRows.map((r) => ({
        label: trendLabel(r, 'hourly', language),
        value: Number(r.revenue) || 0,
    }));
    let peakIdx = -1;
    let peakRev = 0;
    hourlyRows.forEach((r, i) => {
        const v = Number(r.revenue) || 0;
        if (v > peakRev) { peakRev = v; peakIdx = i; }
    });
    const peakLabel = peakIdx >= 0 ? trendLabel(hourlyRows[peakIdx], 'hourly', language) : '';
    const hourlyAria = peakIdx >= 0
        ? `${t('revenue_by_hour', {}, 'Revenue by Hour')} — ${t('peak_hour', {}, 'Peak Hour')}: ${peakLabel}`
        : t('revenue_by_hour', {}, 'Revenue by Hour');

    /* ---- "who is carrying the shift": staff leaderboard by revenue.
       Factual figures only (revenue, share, transactions, avg
       transaction) — no judgemental labels. Names are raw BarList labels,
       NEVER t() keys (an employee named like a registry key would render
       that key's translation instead of their name). ---- */
    const staffRows = [...(sections.staff || [])]
        .sort((a, b) => (Number(b.revenue) || 0) - (Number(a.revenue) || 0));
    const staffTotal = staffRows.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
    const staffItems = staffRows.map((r) => ({
        label: r.staff_name,
        value: Number(r.revenue) || 0,
        pct: staffTotal > 0 ? ((Number(r.revenue) || 0) / staffTotal) * 100 : 0,
        caption: t(
            'ops_staff_caption',
            {
                txns: (Number(r.transactions) || 0).toLocaleString(),
                avg: fmtMoney(r.avg_transaction, currency),
            },
            '{txns} transactions · {avg} avg',
        ),
    }));

    return (
        <div className="ops-wrap">
            <style>{CSS}</style>
            {/* RangeBar sits OUTSIDE SurfaceStates so the range stays
                changeable from the error/empty states. */}
            <RangeBar
                value={preset}
                onChange={(id) => setPreset(id)}
            />
            <PullToRefresh onRefresh={load}>
                <SurfaceStates status={status} onRetry={load}>
                    {/* Hero — Total Revenue + delta vs previous period (kit
                        HeroValue: renders nothing when revNorm is null). */}
                    <HeroValue
                        label={t('total_revenue', {}, 'Total Revenue')}
                        norm={revNorm}
                        currency={currency}
                        testid="ops-hero"
                    />

                    {/* KPI grid — incl. the string Peak Hour tile (no pill). */}
                    {gridItems.length > 0 && (
                        <KpiGrid currency={currency} items={gridItems} />
                    )}

                    {/* Revenue by Hour — the busiest hour marked on the curve;
                        ≥4 points draws the chart, fewer renders the kit's stat
                        card (which carries no marker). */}
                    <TrendChart
                        title={t('revenue_by_hour', {}, 'Revenue by Hour')}
                        ariaLabel={hourlyAria}
                        points={hourlyPoints}
                        formatValue={(v) => fmtMoney(v, currency)}
                        {...(peakIdx >= 0 ? { markerIndex: peakIdx } : {})}
                    />

                    {/* Staff leaderboard — ranked by revenue, values always
                        visible, share of team revenue beside each. */}
                    <BarList
                        title={t('staff_leaderboard', {}, 'Staff Leaderboard')}
                        items={staffItems}
                        formatValue={(v) => fmtMoney(v, currency)}
                    />

                    {/* Insights — top-level data.insights (sibling of
                        sections). NO NarrativeCard: /operations has no
                        narrative endpoint. */}
                    <InsightList items={(data && data.insights) || []} />
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
